import React, { useState, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import FashFastService from '../../services/FashFastService';
import {
    AlertCircle,
    Camera,
    Check,
    ChevronLeft,
    ImagePlus,
    RefreshCw,
    ScanLine,
    ShieldCheck,
    Shirt,
    Sparkles,
    Upload,
    Wand2
} from 'lucide-react';
import './FashFastPage.css';

type Step = 'UPLOAD' | 'VALIDATING' | 'GENERATING' | 'RESULT';

const DEMO_PROFILES = [
  { id: 'melis', name: 'Melis K.', image: '/assets/showcase/avatar_melis.jpg' },
  { id: 'can', name: 'Can Y.', image: '/assets/showcase/avatar_can.jpg' },
  { id: 'emre', name: 'Emre A.', image: '/assets/showcase/avatar_emre.jpg' }
];

const FashFastPage: React.FC = () => {
    const { state, closeWidget, dispatch } = useAppContext();
    const [step, setStep] = useState<Step>('UPLOAD');
    const [originalImageBase64, setOriginalImageBase64] = useState<string | null>(null);
    const [resultImageUrl, setResultImageUrl] = useState<string | null>(null);
    const [comparePosition, setComparePosition] = useState(50);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const productId = state.fashFastProduct?.productId || '';
    const brandId = state.brandId || '';
    
    // Clear product metadata if testing standalone
    const productMeta = state.fashFastProduct?.productMeta || { 
        name: 'Target Product', 
        imageUrl: 'https://via.placeholder.com/150' 
    };

    // Sync demo person if changed via parent postMessage
    React.useEffect(() => {
        if (state.demoMode && state.demoPersonId && step === 'UPLOAD') {
            console.log(`👤 [FashFast] Syncing demo person from state: ${state.demoPersonId}`);
            const personKey = state.demoPersonId;
            const matchedProfile = DEMO_PROFILES.find(p => p.id === personKey);
            if (matchedProfile) {
                setOriginalImageBase64(matchedProfile.image);
                runValidation(matchedProfile.image);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.demoMode, state.demoPersonId]);

    // Helper: resize image on client to save memory
    const preprocessImage = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 1024;
                    const MAX_HEIGHT = 1024;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                        }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.85));
                };
                img.src = e.target?.result as string;
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setError(null);
        setResultImageUrl(null);
        setComparePosition(50);
        try {
            const base64 = await preprocessImage(file);
            setOriginalImageBase64(base64);
            runValidation(base64);
        } catch (err: any) {
            setError(err.message || 'Error processing image');
        }
    };

    const runValidation = async (base64: string) => {
        setStep('VALIDATING');
        if (state.demoMode) {
            console.log('🛡️ [FashFast] Running in demo mode, bypassing validation');
            await new Promise(resolve => setTimeout(resolve, 1500));
            runGeneration(base64);
            return;
        }
        try {
            const response = await FashFastService.validateSelfie(base64);
            if (!response.valid) {
                setError(response.error || 'Invalid photo');
                setStep('UPLOAD');
                return;
            }
            
            // Valid! Auto-progress to GENERATING
            runGeneration(base64);
        } catch (err: any) {
            setError(err.message || 'Validation failed');
            setStep('UPLOAD');
        }
    };

    const runGeneration = async (base64: string, isRegenerate = false) => {
        setStep('GENERATING');
        if (state.demoMode) {
            console.log('🎬 [FashFast] Running in demo mode, returning pre-cached try-on result');
            await new Promise(resolve => setTimeout(resolve, 3000));
            const personKey = state.demoPersonId || 'melis';
            const cacheUrl = `/assets/showcase/fast_${personKey}.jpg`;
            setResultImageUrl(cacheUrl);
            setComparePosition(50);
            setStep('RESULT');
            return;
        }
        try {
            // NOTE: user ID should ideally be from auth state, mocked here
            const responseUrl = await FashFastService.generateTryOn(
                base64, 
                productId, 
                brandId, 
                'temp-user-123',
                isRegenerate
            );
            setResultImageUrl(responseUrl);
            setComparePosition(50);
            setStep('RESULT');
        } catch (err: any) {
            setError(err.message || 'Generation failed');
            setStep('UPLOAD');
        }
    };

    const handleDislike = () => {
        if (!originalImageBase64) return;
        runGeneration(originalImageBase64, true); // True flag = forceRegenerate
    };

    const resetFlow = () => {
        setOriginalImageBase64(null);
        setResultImageUrl(null);
        setComparePosition(50);
        setError(null);
        setStep('UPLOAD');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getStepStatus = (targetStep: Step) => {
        const order: Step[] = ['UPLOAD', 'VALIDATING', 'GENERATING', 'RESULT'];
        const currentIndex = order.indexOf(step);
        const targetIndex = order.indexOf(targetStep);

        if (currentIndex === targetIndex) return 'active';
        if (currentIndex > targetIndex || step === 'RESULT') return 'complete';
        return 'idle';
    };

    return (
        <div className="fashfast-container fashfast-theme">
            <div className="fashfast-header">
                <button className="icon-button" onClick={closeWidget} aria-label="Alışverişe dön">
                    <ChevronLeft size={22} />
                </button>
                <div className="logo-container">
                    <Sparkles size={19} />
                    <h1>Fash<span className="fast-text">Fast</span></h1>
                </div>
                <div className="header-badge">Sanal Kabin</div>
            </div>

            <div className="fashfast-content">
                <div className="flow-shell">
                    <div className="step-track" aria-label="FashFast adımları">
                        <div className={`step-pill ${getStepStatus('UPLOAD')}`}>
                            <ImagePlus size={16} />
                            <span>Fotoğraf</span>
                        </div>
                        <div className={`step-pill ${getStepStatus('VALIDATING')}`}>
                            <ShieldCheck size={16} />
                            <span>Kontrol</span>
                        </div>
                        <div className={`step-pill ${getStepStatus('GENERATING')}`}>
                            <Wand2 size={16} />
                            <span>Deneme</span>
                        </div>
                        <div className={`step-pill ${getStepStatus('RESULT')}`}>
                            <Check size={16} />
                            <span>Sonuç</span>
                        </div>
                    </div>

                {step === 'UPLOAD' && (
                    <div className="upload-section animate-fade-in">
                        <div className="fashfast-info-box">
                            <AlertCircle size={20} />
                            <div className="fashfast-info-box-content">
                                <h4>Önemli Fotoğraf Kuralı</h4>
                                <p>FashFast sanal deneme modelimizin başarılı sonuç üretmesi için yükleyeceğiniz görselin <strong>dışarıda ya da evde çekilmiş boydan (tüm vücut) bir fotoğraf</strong> olması gerekmektedir. Selfie (yakın çekim) veya sadece üst gövde fotoğrafları kabul edilmemektedir.</p>
                            </div>
                        </div>

                        {state.demoMode && (
                            <div className="demo-profiles-container" style={{ width: '100%', maxWidth: '500px', margin: '0 auto 20px auto' }}>
                                <h4 className="demo-profiles-title" style={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', margin: '0 0 12px 0', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hızlı Deneme için Model Seçin:</h4>
                                <div className="demo-profiles-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                                    {DEMO_PROFILES.map(profile => (
                                        <button
                                            key={profile.id}
                                            type="button"
                                            className={`demo-profile-card ${originalImageBase64 === profile.image ? 'active' : ''}`}
                                            onClick={() => {
                                                setOriginalImageBase64(profile.image);
                                                if (dispatch) {
                                                    dispatch({
                                                        type: 'HANDLE_MESSAGE',
                                                        payload: {
                                                            type: 'DEMO_SELECT_PERSON',
                                                            payload: { personId: profile.id }
                                                        }
                                                    });
                                                }
                                                runValidation(profile.image);
                                            }}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                background: '#ffffff',
                                                border: originalImageBase64 === profile.image ? '2px solid #4f46e5' : '2px solid transparent',
                                                backgroundColor: originalImageBase64 === profile.image ? '#f5f3ff' : '#ffffff',
                                                borderRadius: '12px',
                                                padding: '10px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                                            }}
                                        >
                                            <img src={profile.image} alt={profile.name} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', marginBottom: '8px', border: originalImageBase64 === profile.image ? '2px solid #4f46e5' : '2px solid #e5e7eb' }} />
                                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#1f2937' }}>{profile.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="upload-layout">
                            <section className="upload-hero">
                                <div className="section-kicker">
                                    <Camera size={16} />
                                    <span>Tek fotoğrafla sanal deneme</span>
                                </div>
                                <h2 className="gradient-title">Dene. Karşılaştır. Karar ver.</h2>
                                <p className="minimal-copy">Dışarıda veya evde çekilmiş boydan fotoğrafınızı yükleyin. Ürünü üzerinizde görün ve sonucu sürgüyle karşılaştırın (Selfie yüklemeyiniz).</p>
                            </section>

                            <aside className="product-panel" aria-label="Seçili ürün">
                                <div className="product-thumb">
                                    <img src={productMeta.imageUrl} alt={productMeta.name} />
                                </div>
                                <div className="product-copy">
                                    <span>Ürün</span>
                                    <strong>{productMeta.name}</strong>
                                </div>
                                <Shirt size={20} />
                            </aside>
                        </div>

                        {error && (
                            <div className="error-banner animate-shake">
                                <AlertCircle size={20} />
                                <span>{error}</span>
                            </div>
                        )}

                        <button className="fashfast-upload-card" type="button" onClick={() => fileInputRef.current?.click()}>
                            <input 
                                type="file" 
                                accept="image/jpeg,image/png,image/webp" 
                                ref={fileInputRef} 
                                onChange={handleFileChange} 
                                style={{ display: 'none' }} 
                            />
                            
                            <div className="upload-trigger">
                                <div className="upload-icon-wrapper">
                                    <div className="upload-icon">
                                        <Camera size={32} />
                                    </div>
                                    <div className="upload-icon-overlay">
                                        <Upload size={16} />
                                    </div>
                                </div>
                                <h3 className="gradient-title compact-title">Boydan Fotoğraf Yükle</h3>
                                <p className="upload-hint">Dışarıda ya da evde çekilmiş boydan görsel (Selfie yüklemeyiniz).</p>
                            </div>
                        </button>

                        <div className="photo-rules" aria-label="Fotoğraf önerileri">
                            <span>Tek kişi</span>
                            <span>Net ışık</span>
                            <span>Dışarıda veya Evde</span>
                            <span>Boydan (Selfie Değil)</span>
                        </div>
                    </div>
                )}

                {step === 'VALIDATING' && (
                    <div className="loading-section animate-fade-in">
                        <div className="analysis-card">
                            {originalImageBase64 && <img src={originalImageBase64} className="preview-bg" alt="preview" />}
                            <div className="scan-line"></div>
                            <div className="analysis-status">
                                <ScanLine size={18} />
                                <span>Fotoğraf inceleniyor</span>
                            </div>
                        </div>
                        <h2 className="loading-text gradient-title">Fotoğraf okunuyor</h2>
                        <p className="minimal-copy">Kadraj ve duruş kontrolü.</p>
                    </div>
                )}

                {step === 'GENERATING' && (
                    <div className="loading-section animate-fade-in generating-state">
                        <div className="generation-card">
                            <div className="generation-preview">
                                {originalImageBase64 && <img src={originalImageBase64} alt="Yüklenen fotoğraf" />}
                                <div className="tailor-lines">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                            <div className="generation-copy">
                                <Wand2 size={24} />
                                <h2 className="loading-text gradient-title">Stil işleniyor</h2>
                                <p className="minimal-copy">Sonuç birkaç saniye içinde hazır.</p>
                            </div>
                        </div>
                    </div>
                )}

                {step === 'RESULT' && resultImageUrl && (
                    <div className="result-section animate-fade-in">
                        <div className="result-copy">
                            <div>
                                <span className="section-kicker">
                                    <Sparkles size={16} />
                                    Sonuç hazır
                                </span>
                                <h2 className="gradient-title">Önce / sonra</h2>
                            </div>
                            <p className="minimal-copy">Çubuğu sürükleyin, farkı görün.</p>
                        </div>

                        <div className="compare-card">
                            <div className="compare-viewer">
                                {originalImageBase64 && (
                                    <img src={originalImageBase64} alt="Önceki fotoğraf" className="compare-image compare-before" />
                                )}
                                <div
                                    className="compare-after-layer"
                                    style={{ clipPath: `inset(0 ${100 - comparePosition}% 0 0)` }}
                                >
                                    <img src={resultImageUrl} alt="Yeni kıyafetli sonuç" className="compare-image" />
                                </div>
                                <div className="compare-label before-label">Önce</div>
                                <div className="compare-label after-label">Sonra</div>
                                <div className="compare-divider" style={{ left: `${comparePosition}%` }}>
                                    <span></span>
                                </div>
                                <input
                                    className="compare-range"
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={comparePosition}
                                    onChange={(event) => setComparePosition(Number(event.target.value))}
                                    aria-label="Önce ve sonra karşılaştırma çubuğu"
                                />
                                <div className="watermark">FashFast AI</div>
                            </div>
                        </div>

                        <div className="action-buttons">
                            <button className="btn-primary" onClick={closeWidget}>
                                <Check size={20} />
                                <span>Alışverişe dön</span>
                            </button>
                            <div className="secondary-actions">
                                <button className="btn-secondary" onClick={handleDislike}>
                                    <RefreshCw size={18} />
                                    <span>Yeniden dene</span>
                                </button>
                                <button className="btn-text" onClick={resetFlow}>
                                    Yeni fotoğraf
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

export default FashFastPage;
